import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Button, Card, Input } from '../../components/primitives/index.js'
import SEO from '../../components/SEO.jsx'
import { useCustomer } from '../../context/CustomerContext.jsx'
import { customerApi } from '../../lib/customerApi.js'
import './AccountAuth.css'

const KIND_COPY = {
  verify: { title: 'Confirming your email…', done: 'Email confirmed! Your order history is now linked to your account.' },
  magic: { title: 'Signing you in…', done: "You're signed in." },
  reset: { title: 'Choose a new password', done: 'Password updated — you are signed in.' },
}

/**
 * Mirrors Prod's /account/confirm contract: `kind` (verify|magic|reset) +
 * `token` query params. verify/magic auto-consume the token on mount; reset
 * needs a new password first. On success, adopts the returned session and
 * auto-redirects to /account after a short pause.
 */
export default function AccountConfirmPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { adoptSession } = useCustomer()
  const kind = params.get('kind') ?? 'verify'
  const token = params.get('token') ?? ''

  const [state, setState] = useState(kind === 'reset' ? 'form' : 'working')
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [formError, setFormError] = useState('')
  const consumed = useRef(false)

  async function consume(pw) {
    try {
      const res = await customerApi.confirmToken(token, pw)
      adoptSession(res.token, res.customer)
      setResult(res)
      setState('done')
      setTimeout(() => navigate('/account'), 2500)
    } catch (err) {
      setError(err.message ?? 'This link is invalid or has expired.')
      setState('error')
    }
  }

  useEffect(() => {
    if (!token) {
      setError('This link is missing its token.')
      setState('error')
      return
    }
    if (kind !== 'reset' && !consumed.current) {
      consumed.current = true
      consume()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleResetSubmit(e) {
    e.preventDefault()
    setFormError('')
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match.')
      return
    }
    setState('working')
    consume(password)
  }

  const copy = KIND_COPY[kind] ?? KIND_COPY.verify

  return (
    <div className="container account-auth">
      <SEO config={{ title: copy.title, robots: 'noindex, nofollow', titleRaw: true }} />
      <Card className="account-auth__card">
        {state === 'working' && (
          <>
            <h1>{copy.title}</h1>
            <p>One moment&hellip;</p>
          </>
        )}

        {state === 'form' && (
          <>
            <h1>{copy.title}</h1>
            <p>Choose a new password for your account.</p>
            <form onSubmit={handleResetSubmit}>
              <Input label="New password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
              <Input label="Confirm password" type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} error={formError} />
              <Button type="submit" variant="primary" size="lg" className="account-auth__submit">
                Update password
              </Button>
            </form>
          </>
        )}

        {state === 'done' && (
          <>
            <h1>Done</h1>
            <p>{copy.done}</p>
            {result?.linkedOrders > 0 && <p className="account-auth__notice">{result.linkedOrders} past orders were linked to your account.</p>}
            <p className="account-auth__switch">Redirecting to your account&hellip;</p>
          </>
        )}

        {state === 'error' && (
          <>
            <h1>Link expired</h1>
            <p>{error}</p>
            <p className="account-auth__switch">
              {kind === 'reset' ? <Link to="/account/forgot">Request a new link</Link> : <Link to="/account/login">Back to sign in</Link>}
            </p>
          </>
        )}
      </Card>
    </div>
  )
}
